import { DEFAULT_ANCHOR } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/anchor/anchorData';
import { useEffect } from 'react';
import { CHILD_ITEM_OFFSET, CLICK_DISABLE_DURATION } from './constants';
import { calculateScrollPosition, isChildItem } from './utils';
import { AnchorItem } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/anchor/types';

export const useScrollToAnchor = (
  contentWrapperRef: React.RefObject<HTMLDivElement>,
  isClickingRef: React.MutableRefObject<boolean>,
  isInitializingRef: React.MutableRefObject<boolean>,
  setCurrentAnchor: (anchor: string) => void,
  anchorItems: AnchorItem[]
) => {
  // Initialize anchor on mount - scroll to hash position if exists
  useEffect(() => {
    if (!contentWrapperRef.current) {
      return;
    }

    // Wait for drawer to be fully rendered before scrolling
    const scrollToAnchor = () => {
      if (!contentWrapperRef.current) {
        return;
      }

      const scrollContainer =
        contentWrapperRef.current.closest('.ant-drawer-body');
      if (!scrollContainer || !(scrollContainer instanceof HTMLElement)) {
        // Retry if drawer not ready yet
        setTimeout(scrollToAnchor, 50);
        return;
      }

      const currentHref = window.location.hash;
      let targetHref = currentHref || DEFAULT_ANCHOR;
      let targetElement: HTMLElement | null = null;

      // Find the target element - check if it's an HTMLElement
      if (currentHref) {
        const foundElement =
          contentWrapperRef.current.querySelector(currentHref);
        if (foundElement instanceof HTMLElement) {
          targetElement = foundElement;
        }
      }

      // If hash element not found, default to first anchor
      if (!targetElement) {
        targetHref = DEFAULT_ANCHOR;
        const foundElement =
          contentWrapperRef.current.querySelector(DEFAULT_ANCHOR);
        if (foundElement instanceof HTMLElement) {
          targetElement = foundElement;
        }
        window.history.replaceState({}, '', targetHref);
      }

      if (targetElement) {
        // Set flags to prevent scroll detection from overriding
        isClickingRef.current = true;
        isInitializingRef.current = true;

        // Update anchor state immediately before scrolling
        setCurrentAnchor(targetHref);
        window.history.replaceState({}, '', targetHref);

        // Use the same scroll calculation as click handler for consistency
        const targetRect = targetElement.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const currentScrollTop = scrollContainer.scrollTop;
        const maxScrollTop =
          scrollContainer.scrollHeight - scrollContainer.clientHeight;
        // Get the actual padding-top of the scroll container
        const containerStyles = window.getComputedStyle(scrollContainer);
        const containerPaddingTop = parseFloat(containerStyles.paddingTop) || 0;

        const calculatedScrollTop = calculateScrollPosition(
          targetRect.top,
          containerRect.top,
          currentScrollTop,
          containerPaddingTop
        );

        const finalScrollTop = Math.min(
          Math.max(
            0,
            calculatedScrollTop +
              (isChildItem(anchorItems, targetHref) ? CHILD_ITEM_OFFSET : 0)
          ),
          maxScrollTop
        );

        // Scroll instantly to the position
        scrollContainer.scrollTo({
          top: finalScrollTop,
          behavior: 'instant',
        });

        // Reset flags after initialization completes - use longer delay to ensure scroll completes
        setTimeout(() => {
          isClickingRef.current = false;
          // Keep initialization flag a bit longer to prevent scroll detection from overriding
          setTimeout(() => {
            isInitializingRef.current = false;
          }, 200);
        }, CLICK_DISABLE_DURATION);
      } else {
        // Even if element not found, mark initialization as complete
        isInitializingRef.current = false;
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Add small delay to ensure drawer animation completes
      setTimeout(scrollToAnchor, 100);
    });
  }, [
    anchorItems,
    contentWrapperRef,
    isClickingRef,
    isInitializingRef,
    setCurrentAnchor,
  ]);
};
